/**
 * Retenue à la source (RAS) — Tunisie
 *
 * Applicable uniquement si :
 * - Le tiers est de type 'company' (pas 'individual')
 * - Le montant TTC dépasse le seuil (1000 DT par défaut)
 *
 * Taux : variable selon le type d'opération (RS1_000001 … RS1_000014)
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Type d'opération RS tel que stocké dans rs_operation_types.
 * Miroir du DTO côté interfaces — reproduit ici pour rester pur TypeScript
 * sans import de la couche data-access.
 */
export interface RSOperationType {
  id: string;
  code: string;           // ex "RS1_000001"
  labelFr: string;
  labelAr: string | null;
  labelEn: string | null;
  defaultRate: number;    // %, ex 1.00 / 3.00 / 15.00
  threshold: number | null;
  appliesToSale: boolean;
  appliesToPurchase: boolean;
  displayOrder: number;
  isActive: boolean;
}

export interface WithholdingLineInput {
  montantTtc: number;
  operationType: RSOperationType;
}

export interface WithholdingLineResult {
  montantRs: number;
  montantNetServi: number;
  tauxRs: number;
}

export interface CertificateTotals {
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  totalRas: number;
  totalNetServi: number;
}

export interface CertificateLineForTotals {
  montantHt: number;
  montantTva: number;
  montantTtc: number;
  montantRs: number;
  montantNetServi: number;
}

// ─── Fonctions existantes (rétrocompat) ──────────────────────────────────────

export function isWithholdingApplicable(
  thirdPartyType: 'company' | 'individual',
  totalTtc: number,
  threshold: number = 1000,
): boolean {
  return thirdPartyType === 'company' && totalTtc > threshold;
}

/**
 * Calcul simple (1 taux, 1 montant TTC).
 * @deprecated Préférer calculateWithholdingForLine pour le multi-taux.
 */
export function calculateWithholding(
  totalTtc: number,
  rate: number = 1,
): number {
  return round3(totalTtc * rate / 100);
}

// ─── Nouvelles fonctions multi-taux ──────────────────────────────────────────

/**
 * Choisit le type d'opération RS par défaut selon le contexte.
 *
 * Logique (simplifiée, à affiner avec les règles DGI complètes) :
 * - Si le tiers est un particulier → null (pas de RAS)
 * - Si montant TTC < seuil du type sélectionné → null
 * - Sinon → retourne le premier type applicable au contexte
 *
 * En pratique, RS1_000001 (1%, >= 1000 DT) est le cas général.
 * C'est le type qui sera sélectionné par défaut dans l'UI.
 */
export function pickDefaultOperationType(
  thirdPartyType: 'company' | 'individual',
  totalTtc: number,
  context: 'sale' | 'purchase',
  knownTypes: RSOperationType[],
): RSOperationType | null {
  if (thirdPartyType === 'individual') return null;

  const applicable = knownTypes.filter((t) => {
    if (!t.isActive) return false;
    if (context === 'sale' && !t.appliesToSale) return false;
    if (context === 'purchase' && !t.appliesToPurchase) return false;
    if (t.threshold !== null && totalTtc <= t.threshold) return false;
    return true;
  });

  if (applicable.length === 0) return null;

  // Préférence : RS1_000001 (cas général) ; sinon premier par display_order
  const preferred = applicable.find((t) => t.code === 'RS1_000001');
  return preferred ?? (applicable.sort((a, b) => a.displayOrder - b.displayOrder)[0] ?? null);
}

/**
 * Calcule le montant RS et le net servi pour une ligne donnée,
 * en utilisant le taux du type d'opération fourni.
 */
export function calculateWithholdingForLine(
  montantTtc: number,
  operationType: Pick<RSOperationType, 'defaultRate'>,
): WithholdingLineResult {
  const tauxRs = operationType.defaultRate;
  const montantRs = round3(montantTtc * tauxRs / 100);
  const montantNetServi = round3(montantTtc - montantRs);
  return { montantRs, montantNetServi, tauxRs };
}

/**
 * Agrège les totaux d'un certificat à partir de ses lignes.
 * Compatible multi-taux (chaque ligne peut avoir un taux différent).
 */
export function calculateCertificateTotals(
  lines: CertificateLineForTotals[],
): CertificateTotals {
  return lines.reduce<CertificateTotals>(
    (acc, l) => ({
      totalHt:        round3(acc.totalHt        + l.montantHt),
      totalVat:       round3(acc.totalVat       + l.montantTva),
      totalTtc:       round3(acc.totalTtc       + l.montantTtc),
      totalRas:       round3(acc.totalRas       + l.montantRs),
      totalNetServi:  round3(acc.totalNetServi  + l.montantNetServi),
    }),
    { totalHt: 0, totalVat: 0, totalTtc: 0, totalRas: 0, totalNetServi: 0 },
  );
}

// ─── Utilitaires ─────────────────────────────────────────────────────────────

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
